import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { Container, Plane, Truck, Boxes, Layers, Loader2 } from "lucide-react";
import { useAnalytics } from "@/lib/useAnalytics";
import { KpiCard } from "@/components/KpiCard";
import { ChartCard, SectionTitle } from "@/components/ChartCard";
import { fmtInt, fmtNum, CHART, tooltipStyle } from "@/lib/format";

const OriginBar = ({ title, subtitle, data, color, unit, testId }) => (
  <ChartCard testId={testId} title={title} subtitle={subtitle}>
    {data.length === 0 ? (
      <div className="flex h-[240px] items-center justify-center text-sm text-muted-foreground">No data for this period</div>
    ) : (
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} margin={{ left: -8, right: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis dataKey="origin" tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" style={{ fontSize: 11 }} />
          <YAxis tickLine={false} axisLine={false} stroke="hsl(var(--muted-foreground))" />
          <Tooltip contentStyle={tooltipStyle} formatter={(v) => `${fmtNum(v)} ${unit}`} cursor={{ fill: "hsl(var(--secondary))" }} />
          <Bar dataKey="value" name={unit} fill={color} radius={[3, 3, 0, 0]} barSize={40} />
        </BarChart>
      </ResponsiveContainer>
    )}
  </ChartCard>
);

const Volume = () => {
  const { data, isLoading } = useAnalytics("volume", "/analytics/volume");
  if (isLoading || !data)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );

  const t = data.totals;

  return (
    <div>
      <SectionTitle title="Volume" description="Freight volume by mode and origin — TEUs, kilos and CBM" />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-5">
        <KpiCard testId="kpi-sea-teu" label="Sea Freight TEUs" value={fmtNum(t.sea_teu)} icon={Container} info="Total sea volume in Twenty-foot Equivalent Units (TEUs) — the standard container measure." />
        <KpiCard testId="kpi-air-kg" label="Air Freight (kg)" value={fmtInt(t.air_kg)} icon={Plane} info="Total chargeable weight of air shipments in kilograms." />
        <KpiCard testId="kpi-road-cbm" label="Road Freight CBM" value={fmtNum(t.road_cbm)} sub="Derived (no CargoWise road feed)" icon={Truck} info="Road freight volume in cubic metres (CBM). Derived estimate — road movements are not in the CargoWise extract." />
        <KpiCard testId="kpi-lcl-cbm" label="LCL CBM" value={fmtNum(t.lcl_cbm)} icon={Boxes} info="Less-than-Container-Load volume in cubic metres (CBM) — cargo sharing a container." />
        <KpiCard testId="kpi-cfs-cbm" label="CFS / Buyer's Consol. CBM" value={fmtNum(t.cfs_cbm)} icon={Layers} info="Container Freight Station / Buyer's Consolidation volume in cubic metres (CBM) — cargo consolidated for the buyer." />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <OriginBar testId="chart-sea-teu" title="Sea Freight TEUs by Origin" subtitle="20ft-equivalent units" data={data.sea_teu_by_origin} color={CHART.sea} unit="TEU" />
        <OriginBar testId="chart-air-kg" title="Air Freight Kilos by Origin" subtitle="Chargeable weight" data={data.air_kg_by_origin} color={CHART.air} unit="kg" />
        <OriginBar testId="chart-lcl-cbm" title="LCL CBM by Origin" subtitle="Less-than-container load" data={data.lcl_cbm_by_origin} color={CHART.lcl} unit="CBM" />
        <OriginBar testId="chart-cfs-cbm" title="CFS / Buyer's Consolidation by Origin" subtitle="Consolidated cargo volume" data={data.cfs_cbm_by_origin} color={CHART.cfs} unit="CBM" />
      </div>

      <div className="mt-6">
        <OriginBar testId="chart-road-cbm" title="Road Freight CBM by Origin" subtitle="Derived estimate — not present in CargoWise extract (mock data)" data={data.road_cbm_by_origin} color={CHART.road} unit="CBM" />
      </div>
    </div>
  );
};

export default Volume;
